import type { FC, ForwardRefExoticComponent, PropsWithoutRef, ReactElement } from 'react'
import { forwardRef } from 'react'
import { Suspense, useMemo } from 'react'
import { Await, useLoaderData } from 'react-router-dom'

import ErrorPage from '@app/shared/errorPage'
import SuspenseLoader from '@app/shared/loaders/suspenseLoader'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withForwardRefAwait = <E extends HTMLElement, P extends PropsWithoutRef<any>>(
  Component: ForwardRefExoticComponent<P>,
  field: string,
  fallback: ReactElement = <SuspenseLoader />
) =>
  forwardRef<E, P>((props, ref) => {
    const loaderData = useLoaderData()
    const fieldData = loaderData[field]
    const memoizedComponent = useMemo(() => <Component ref={ref} {...props} />, [props, ref])

    return (
      <Suspense fallback={fallback}>
        <Await resolve={fieldData} errorElement={<ErrorPage />}>
          {memoizedComponent}
        </Await>
      </Suspense>
    )
  })

const withAwait =
  (Component: FC, field: string, fallback: ReactElement = <SuspenseLoader />) =>
  (props): ReactElement => {
    const loaderData = useLoaderData()
    const fieldData = loaderData[field]
    const memoizedComponent = useMemo(() => <Component {...props} />, [props])

    return (
      <Suspense fallback={fallback}>
        <Await resolve={fieldData} errorElement={<ErrorPage />}>
          {memoizedComponent}
        </Await>
      </Suspense>
    )
  }

export default withAwait
